import { useState, useEffect, useRef, useCallback } from "react";
import { Reorder, AnimatePresence, motion } from "framer-motion";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  LayoutDashboard, Eye, EyeOff, Save, Loader2, Plus, Trash2, GripVertical,
  ShoppingBag, Settings2, ChevronDown, Image as ImageIcon, Type, SeparatorHorizontal,
  AlignLeft, AlignCenter, AlignRight, Layers, Palette, X, ExternalLink,
  Store, Package, Zap, Tag, Monitor, ToggleLeft, Move,
  Sparkles, Calendar, LayoutGrid, RefreshCw, Check,
} from "lucide-react";

// â”€â”€ ID generator â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const uid = () => Math.random().toString(36).slice(2, 10);

// â”€â”€ Block type catalog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BLOCK_TYPES = [
  { type: "events_hero",      label: "Banners de eventos",   icon: Calendar,   color: "bg-purple-100 text-purple-600",  desc: "Slider con los eventos activos" },
  { type: "auctions_banner",  label: "Subastas activas",     icon: Zap,        color: "bg-red-100 text-red-500",        desc: "Banner de subastas en vivo" },
  { type: "category_section", label: "Sección por categoría",icon: LayoutGrid, color: "bg-blue-100 text-blue-600",     desc: "Tiendas de una categoría específica" },
  { type: "offers_section",   label: "Sección de ofertas",   icon: Tag,        color: "bg-orange-100 text-orange-500", desc: "Carrusel de productos con descuento" },
  { type: "stores_section",   label: "Más tiendas",          icon: Store,      color: "bg-green-100 text-green-600",   desc: "Todas las tiendas aliadas" },
  { type: "products_grid",    label: "Grilla de productos",  icon: Package,    color: "bg-teal-100 text-teal-600",     desc: "Todos los productos con filtro y scroll infinito" },
  { type: "text_block",       label: "Bloque de texto",      icon: Type,       color: "bg-yellow-100 text-yellow-600", desc: "Título, subtítulo o párrafo personalizado" },
  { type: "banner_image",     label: "Banner con imagen",    icon: ImageIcon,  color: "bg-pink-100 text-pink-500",     desc: "Imagen personalizada con enlace opcional" },
  { type: "divider",          label: "Separador",            icon: SeparatorHorizontal, color: "bg-gray-100 text-gray-500", desc: "Línea divisoria entre secciones" },
  { type: "spacer",           label: "Espacio en blanco",    icon: Move,       color: "bg-gray-100 text-gray-400",    desc: "Espacio vacío configurable" },
];

// â”€â”€ Default block configs per type â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DEFAULT_BLOCK_CONFIG = {
  events_hero:      { height: "md" },
  auctions_banner:  {},
  category_section: { categoryId: "", label: "", emoji: "ðŸ½ï¸", showOffers: true },
  offers_section:   { title: "🔥 Descuentos HOY", limit: 20 },
  stores_section:   { title: "🏪 Más tiendas" },
  products_grid:    {},
  text_block:       { text: "Tu texto aquí", fontSize: "xl", fontWeight: "bold", align: "left", color: "#111827", backgroundColor: "transparent", padding: "md" },
  banner_image:     { imageBase64: "", link: "", height: "md", borderRadius: "xl", objectFit: "cover" },
  divider:          { style: "solid", color: "#e5e7eb", spacing: "md" },
  spacer:           { height: "md" },
};

const DEFAULT_THEME = {
  primaryColor: "#f59e0b",
  cardRadius: "2xl",
  cardShadow: true,
  showCardTitles: true,
};

const DEFAULT_CONFIG = {
  showEventBanners:      true,
  showDailyOffers:       true,
  showCardTitles:        true,
  sections:              [],
  useBlocks:             false,
  blocks:                [],
  theme:                 DEFAULT_THEME,
  productGridCategoryId: null,
};

const EMOJIS = ["ðŸ½ï¸","🛒","ðŸ’Š","ðŸ‘—","ðŸ“±","🏪","ðŸ•","ðŸ¥—","â˜•","ðŸ”","ðŸŽ","ðŸ›ï¸","ðŸ‹ï¸","ðŸŽ®","ðŸ§´","ðŸŒ®","ðŸ¥©","ðŸœ","ðŸ¥¤","ðŸ "];

const CARD_RADII = [
  { val: "none", label: "Sin redondeo" },
  { val: "md",   label: "Poco" },
  { val: "xl",   label: "Moderado" },
  { val: "2xl",  label: "Redondeado" },
  { val: "3xl",  label: "Muy redondeado" },
];

// â”€â”€ Toggle switch â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function Toggle({ value, onChange, colorOn = "bg-supernova-500" }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 rounded-full transition-all shrink-0 ${value ? colorOn : "bg-gray-200"}`}
    >
      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${value ? "left-5" : "left-0.5"}`} />
    </button>
  );
}

// â”€â”€ Image uploader â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function ImageUploader({ value, onChange, label = "Imagen" }) {
  const ref = useRef();
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1.5 block font-medium">{label}</label>
      <div className="flex gap-2 items-center">
        {value && (
          <div className="relative w-16 h-10 rounded-lg overflow-hidden border border-gray-200 shrink-0">
            <img src={value} alt="preview" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition"
            >
              <X size={12} className="text-white" />
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-gray-300 rounded-xl text-xs text-gray-500 hover:border-supernova-400 hover:bg-supernova-50 transition flex-1"
        >
          <ImageIcon size={12} /> {value ? "Cambiar imagen" : "Subir imagen"}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}

// â”€â”€ Block settings panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BlockSettings({ block, onChange, categories }) {
  const cfg = block.config ?? {};
  const set = (key, val) => onChange({ ...block, config: { ...cfg, [key]: val } });

  if (block.type === "events_hero") return (
    <div className="space-y-3">
      <label className="text-xs text-gray-500 font-medium block">Altura del banner</label>
      <div className="grid grid-cols-4 gap-1.5">
        {[["sm","Pequeño"],["md","Mediano"],["lg","Grande"],["xl","Extra grande"]].map(([val, label]) => (
          <button key={val} type="button"
            onClick={() => set("height", val)}
            className={`py-1.5 text-xs font-semibold rounded-lg border-2 transition ${cfg.height === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
          >{label}</button>
        ))}
      </div>
    </div>
  );

  if (block.type === "category_section") return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Categoría</label>
        <select
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
          value={cfg.categoryId ?? ""}
          onChange={e => set("categoryId", e.target.value)}
        >
          <option value="">Seleccionar categoría...</option>
          {categories.map(c => <option key={c.id ?? c._id} value={c.id ?? c._id}>{c.name}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Etiqueta</label>
          <input
            type="text"
            value={cfg.label ?? ""}
            onChange={e => set("label", e.target.value)}
            placeholder="Ej: Restaurantes"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Emoji</label>
          <select
            className="border border-gray-200 rounded-xl px-2 py-2 text-base outline-none focus:ring-2 focus:ring-supernova-300"
            value={cfg.emoji ?? "ðŸ½ï¸"}
            onChange={e => set("emoji", e.target.value)}
          >
            {EMOJIS.map(em => <option key={em} value={em}>{em}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5">
        <span className="text-xs text-gray-600 font-medium">Mostrar ofertas de esta categoría</span>
        <Toggle value={cfg.showOffers ?? true} onChange={v => set("showOffers", v)} colorOn="bg-orange-400" />
      </div>
    </div>
  );

  if (block.type === "offers_section") return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Título de la sección</label>
        <input
          type="text"
          value={cfg.title ?? ""}
          onChange={e => set("title", e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Cantidad máxima de productos: <span className="text-gray-800 font-bold">{cfg.limit ?? 20}</span></label>
        <input type="range" min="4" max="40" step="4"
          value={cfg.limit ?? 20}
          onChange={e => set("limit", Number(e.target.value))}
          className="w-full accent-supernova-500"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-0.5"><span>4</span><span>40</span></div>
      </div>
    </div>
  );

  if (block.type === "stores_section") return (
    <div>
      <label className="text-xs text-gray-500 font-medium block mb-1.5">Título de la sección</label>
      <input
        type="text"
        value={cfg.title ?? ""}
        onChange={e => set("title", e.target.value)}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
      />
    </div>
  );

  if (block.type === "text_block") return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Texto</label>
        <textarea
          rows={3}
          value={cfg.text ?? ""}
          onChange={e => set("text", e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300 resize-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Tamaño de fuente</label>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
            value={cfg.fontSize ?? "xl"}
            onChange={(e) => set("fontSize", e.target.value)}
          >
            {[["sm","Pequeño"],["base","Normal"],["lg","Mediano"],["xl","Grande"],["2xl","Muy grande"],["3xl","Extra grande"],["4xl","Enorme"]].map(([val,lab]) => (
              <option key={val} value={val}>{lab}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Peso de fuente</label>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
            value={cfg.fontWeight ?? "bold"}
            onChange={e => set("fontWeight", e.target.value)}
          >
            {[["normal","Normal"],["medium","Medio"],["semibold","Semi negrita"],["bold","Negrita"],["black","Extra negrita"]].map(([val,lab]) => (
              <option key={val} value={val}>{lab}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Alineación</label>
        <div className="flex gap-2">
          {[["left", AlignLeft, "Izquierda"], ["center", AlignCenter, "Centro"], ["right", AlignRight, "Derecha"]].map(([val, Icon, lab]) => (
            <button key={val} type="button"
              onClick={() => set("align", val)}
              title={lab}
              className={`flex-1 py-2 flex items-center justify-center rounded-xl border-2 transition ${cfg.align === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Color del texto</label>
          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-1.5">
            <input type="color" value={cfg.color ?? "#111827"}
              onChange={e => set("color", e.target.value)}
              className="w-6 h-6 rounded cursor-pointer border-0"
            />
            <span className="text-xs text-gray-600 font-mono">{cfg.color ?? "#111827"}</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Fondo</label>
          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-1.5">
            <input type="color" value={cfg.backgroundColor === "transparent" ? "#ffffff" : (cfg.backgroundColor ?? "#ffffff")}
              onChange={e => set("backgroundColor", e.target.value)}
              className="w-6 h-6 rounded cursor-pointer border-0"
            />
            <button type="button" onClick={() => set("backgroundColor", "transparent")}
              className={`text-xs px-1.5 py-0.5 rounded-md transition ${cfg.backgroundColor === "transparent" ? "bg-gray-200 text-gray-700" : "text-gray-400 hover:bg-gray-100"}`}
            >
              Ninguno
            </button>
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Espaciado interno</label>
        <div className="grid grid-cols-4 gap-1.5">
          {[["none","Sin"],["sm","Poco"],["md","Medio"],["lg","Amplio"]].map(([val, lab]) => (
            <button key={val} type="button"
              onClick={() => set("padding", val)}
              className={`py-1.5 text-xs font-semibold rounded-lg border-2 transition ${cfg.padding === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            >{lab}</button>
          ))}
        </div>
      </div>
    </div>
  );

  if (block.type === "banner_image") return (
    <div className="space-y-3">
      <ImageUploader
        label="Imagen del banner"
        value={cfg.imageBase64 ?? ""}
        onChange={v => set("imageBase64", v)}
      />
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Enlace al hacer clic (URL o ruta)</label>
        <input
          type="text"
          value={cfg.link ?? ""}
          onChange={e => set("link", e.target.value)}
          placeholder="https://... o /ruta-interna"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Altura</label>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
            value={cfg.height ?? "md"}
            onChange={e => set("height", e.target.value)}
          >
            {[["sm","Pequeño (120px)"],["md","Mediano (180px)"],["lg","Grande (240px)"],["xl","Extra grande (320px)"]].map(([v,l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Ajuste de imagen</label>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
            value={cfg.objectFit ?? "cover"}
            onChange={e => set("objectFit", e.target.value)}
          >
            <option value="cover">Cubrir (cover)</option>
            <option value="contain">Contener (contain)</option>
            <option value="fill">Estirar (fill)</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Bordes redondeados</label>
        <div className="grid grid-cols-3 gap-1.5">
          {[["none","Sin"],["md","Poco"],["xl","Medio"],["2xl","Redondo"],["3xl","Muy redondo"],["full","Circular"]].map(([val,lab]) => (
            <button key={val} type="button"
              onClick={() => set("borderRadius", val)}
              className={`py-1.5 text-xs font-semibold rounded-lg border-2 transition ${cfg.borderRadius === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            >{lab}</button>
          ))}
        </div>
      </div>
    </div>
  );

  if (block.type === "divider") return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Estilo de línea</label>
        <div className="grid grid-cols-3 gap-1.5">
          {[["solid","Sólida"],["dashed","Discontinua"],["dotted","Punteada"]].map(([val,lab]) => (
            <button key={val} type="button"
              onClick={() => set("style", val)}
              className={`py-2 text-xs font-semibold rounded-xl border-2 transition ${cfg.style === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            >{lab}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Color de la línea</label>
        <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-1.5">
          <input type="color" value={cfg.color ?? "#e5e7eb"}
            onChange={e => set("color", e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border-0"
          />
          <span className="text-xs text-gray-600 font-mono">{cfg.color ?? "#e5e7eb"}</span>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 font-medium block mb-1.5">Espaciado</label>
        <div className="grid grid-cols-4 gap-1.5">
          {[["sm","Mínimo"],["md","Medio"],["lg","Grande"],["xl","Amplio"]].map(([val,lab]) => (
            <button key={val} type="button"
              onClick={() => set("spacing", val)}
              className={`py-1.5 text-xs font-semibold rounded-lg border-2 transition ${cfg.spacing === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
            >{lab}</button>
          ))}
        </div>
      </div>
    </div>
  );

  if (block.type === "spacer") return (
    <div className="space-y-3">
      <label className="text-xs text-gray-500 font-medium block">Altura del espacio</label>
      <div className="grid grid-cols-4 gap-1.5">
        {[["sm","8px"],["md","16px"],["lg","32px"],["xl","64px"]].map(([val,lab]) => (
          <button key={val} type="button"
            onClick={() => set("height", val)}
            className={`py-2 text-xs font-semibold rounded-xl border-2 transition flex flex-col items-center gap-0.5 ${cfg.height === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
          >
            <span className="text-[10px] uppercase font-bold">{val}</span>
            <span className="text-[9px] opacity-70">{lab}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // auctions_banner, products_grid — no settings needed
  return (
    <p className="text-xs text-gray-400 italic">Este bloque no tiene configuración adicional.</p>
  );
}

// â”€â”€ Single block card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BlockCard({ block, onToggle, onDelete, onChange, onDuplicate, categories, expandedId, setExpandedId }) {
  const meta = BLOCK_TYPES.find(t => t.type === block.type) ?? {
    label: block.type, icon: Layers, color: "bg-gray-100 text-gray-500",
  };
  const Icon = meta.icon;
  const isExpanded = expandedId === block.id;

  return (
    <Reorder.Item
      value={block}
      dragListener={!isExpanded}
      className={`bg-white rounded-2xl border transition-all overflow-hidden ${isExpanded ? "border-supernova-300 shadow-md" : "border-gray-200 shadow-sm hover:border-gray-300"}`}
    >
      {/* Header row */}
      <div className={`flex items-center gap-3 px-4 py-3 ${!block.visible ? "opacity-50" : ""}`}>
        <GripVertical size={16} className="text-gray-300 cursor-grab active:cursor-grabbing shrink-0" />
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
          <Icon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">{meta.label}</p>
          {block.config?.label && (
            <p className="text-[10px] text-gray-400 truncate">{block.config.label}</p>
          )}
          {block.config?.text && !block.config?.label && (
            <p className="text-[10px] text-gray-400 truncate">{block.config.text}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Settings */}
          {!["auctions_banner","products_grid"].includes(block.type) && (
            <button type="button"
              onClick={() => setExpandedId(isExpanded ? null : block.id)}
              title="Configurar"
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition ${isExpanded ? "bg-supernova-100 text-supernova-600" : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"}`}
            >
              <Settings2 size={13} />
            </button>
          )}
          {/* Duplicate */}
          <button type="button"
            onClick={onDuplicate}
            title="Duplicar"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            <RefreshCw size={12} />
          </button>
          {/* Visibility */}
          <button type="button"
            onClick={onToggle}
            title={block.visible ? "Ocultar" : "Mostrar"}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            {block.visible ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
          {/* Delete */}
          <button type="button"
            onClick={onDelete}
            title="Eliminar"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:bg-red-50 hover:text-red-400 transition"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Settings panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            key="settings"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-gray-100 bg-gray-50/50">
              <BlockSettings block={block} onChange={onChange} categories={categories} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Reorder.Item>
  );
}

// â”€â”€ Block palette modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BlockPalette({ onAdd, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: -8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -8 }}
      transition={{ duration: 0.15 }}
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 z-50 p-3 grid grid-cols-2 gap-2"
    >
      {BLOCK_TYPES.map(({ type, label, icon: Icon, color, desc }) => (
        <button
          key={type}
          type="button"
          onClick={() => { onAdd(type); onClose(); }}
          className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 text-left transition group"
        >
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
            <Icon size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-gray-800 leading-tight">{label}</p>
            <p className="text-[10px] text-gray-400 leading-snug line-clamp-2">{desc}</p>
          </div>
        </button>
      ))}
    </motion.div>
  );
}

// â”€â”€ Classic mode (legacy sections) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const GLOBAL_SECTIONS = [
  { key: "showEventBanners", label: "Banners de eventos",      desc: "Carrusel de eventos activos en la parte superior" },
  { key: "showDailyOffers",  label: "Ofertas del día",         desc: "Carrusel de productos en oferta" },
  { key: "showCardTitles",   label: "Títulos sobre imágenes",  desc: "Nombre del negocio superpuesto en las tarjetas" },
];

function ClassicMode({ config, setConfig, categories }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newSec, setNewSec]   = useState({ categoryId: "", label: "", emoji: "ðŸ½ï¸", showOffers: true, visible: true });

  const toggleGlobal = (key) => setConfig(p => ({ ...p, [key]: !p[key] }));
  const toggleSection = (idx) => setConfig(p => {
    const s = [...p.sections]; s[idx] = { ...s[idx], visible: !s[idx].visible }; return { ...p, sections: s };
  });
  const toggleOffers = (idx) => setConfig(p => {
    const s = [...p.sections]; s[idx] = { ...s[idx], showOffers: !s[idx].showOffers }; return { ...p, sections: s };
  });
  const removeSection = (idx) => setConfig(p => ({ ...p, sections: p.sections.filter((_, i) => i !== idx) }));
  const addSection = () => {
    if (!newSec.categoryId) { toast.warning("Selecciona una categoría"); return; }
    if (config.sections.some(s => s.categoryId === newSec.categoryId)) { toast.warning("Esta categoría ya está agregada"); return; }
    const cat = categories.find(c => (c.id ?? c._id) === newSec.categoryId);
    setConfig(p => ({
      ...p,
      sections: [...p.sections, { ...newSec, label: newSec.label || cat?.name || "Sección", order: p.sections.length }],
    }));
    setNewSec({ categoryId: "", label: "", emoji: "ðŸ½ï¸", showOffers: true, visible: true });
    setShowAdd(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
        {GLOBAL_SECTIONS.map(({ key, label, desc }) => {
          const active = config[key];
          return (
            <div key={key} className="flex items-center justify-between px-5 py-4">
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-supernova-100" : "bg-gray-100"}`}>
                  {active ? <Eye size={16} className="text-supernova-600" /> : <EyeOff size={16} className="text-gray-400" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${active ? "text-gray-900" : "text-gray-400"}`}>{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                </div>
              </div>
              <Toggle value={active} onChange={() => toggleGlobal(key)} />
            </div>
          );
        })}
      </div>

      {/* Categoría por defecto del grid de productos */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
            <Package size={15} className="text-teal-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Productos del inicio</p>
            <p className="text-xs text-gray-400">Categoría que se muestra por defecto en el grid de productos</p>
          </div>
        </div>
        <div className="px-5 py-4">
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-supernova-300 bg-white"
            value={config.productGridCategoryId ?? ""}
            onChange={e => setConfig(p => ({ ...p, productGridCategoryId: e.target.value || null }))}
          >
            <option value="">Todos los productos (sin filtro)</option>
            {categories.map(c => (
              <option key={c.id ?? c._id} value={c.id ?? c._id}>{c.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-2 leading-relaxed">
            Al abrir el inicio, el grid mostrará solo productos de esta categoría. El usuario puede cambiarla usando las pastillas de filtro.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Secciones por categoría</p>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1.5 text-sm font-semibold text-supernova-600 hover:text-supernova-700">
          <Plus size={16} /> Agregar
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-2xl border border-supernova-200 p-4 space-y-3">
          <div className="flex gap-2">
            <select className="flex-1 border rounded-xl p-2.5 text-sm" value={newSec.categoryId} onChange={e => setNewSec({ ...newSec, categoryId: e.target.value })}>
              <option value="">Seleccionar categoría...</option>
              {categories.map(c => <option key={c.id ?? c._id} value={c.id ?? c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <input className="flex-1 border rounded-xl p-2.5 text-sm" placeholder="Etiqueta" value={newSec.label} onChange={e => setNewSec({ ...newSec, label: e.target.value })} />
            <select className="border rounded-xl p-2.5 text-base" value={newSec.emoji} onChange={e => setNewSec({ ...newSec, emoji: e.target.value })}>
              {EMOJIS.map(em => <option key={em} value={em}>{em}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={newSec.showOffers} onChange={e => setNewSec({ ...newSec, showOffers: e.target.checked })} className="accent-supernova-500" />
            Mostrar ofertas de esta categoría
          </label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
            <button onClick={addSection} className="px-4 py-2 text-sm font-semibold bg-supernova-500 text-white rounded-xl hover:bg-supernova-600">Agregar</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
        {config.sections.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-400 text-sm">
            No hay secciones configuradas.<br />
            <span className="text-xs">Agrega una para mostrar tiendas por categoría.</span>
          </div>
        ) : config.sections.map((section, idx) => {
          const cat = categories.find(c => (c.id ?? c._id) === section.categoryId);
          return (
            <div key={section.categoryId} className="px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GripVertical size={16} className="text-gray-300 shrink-0" />
                  <span className="text-lg">{section.emoji}</span>
                  <div>
                    <p className={`text-sm font-semibold ${section.visible ? "text-gray-900" : "text-gray-400"}`}>{section.label || cat?.name}</p>
                    <p className="text-xs text-gray-400">{cat?.name ?? section.categoryId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Toggle value={section.visible} onChange={() => toggleSection(idx)} />
                  <button onClick={() => removeSection(idx)} className="text-gray-300 hover:text-red-400 transition"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="mt-2 ml-8 flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                <div className="flex items-center gap-2">
                  <ShoppingBag size={13} className="text-gray-400" />
                  <span className="text-xs text-gray-500">Mostrar ofertas de esta categoría</span>
                </div>
                <Toggle value={section.showOffers} onChange={() => toggleOffers(idx)} colorOn="bg-orange-400" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// â”€â”€ Theme settings tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function ThemeTab({ theme, onChange }) {
  const set = (key, val) => onChange({ ...theme, [key]: val });
  return (
    <div className="space-y-5">
      {/* Primary color */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-4">
        <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2"><Palette size={15} className="text-supernova-500" /> Colores</h4>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1.5">Color principal (botones, acentos)</label>
          <div className="flex items-center gap-3">
            <input type="color" value={theme.primaryColor ?? "#f59e0b"}
              onChange={e => set("primaryColor", e.target.value)}
              className="w-10 h-10 rounded-xl cursor-pointer border border-gray-200"
            />
            <div>
              <p className="text-sm font-bold" style={{ color: theme.primaryColor ?? "#f59e0b" }}>
                {theme.primaryColor ?? "#f59e0b"}
              </p>
              <p className="text-xs text-gray-400">Se aplica en botones y acentos visuales</p>
            </div>
            <button type="button" onClick={() => set("primaryColor", "#f59e0b")}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-100 transition">
              <RefreshCw size={10} /> Reset
            </button>
          </div>
          {/* Color presets */}
          <div className="flex gap-2 mt-3 flex-wrap">
            {["#f59e0b","#ef4444","#8b5cf6","#06b6d4","#10b981","#f97316","#ec4899","#64748b","#1d4ed8","#000000"].map(c => (
              <button key={c} type="button"
                onClick={() => set("primaryColor", c)}
                className={`w-7 h-7 rounded-lg border-2 transition ${theme.primaryColor === c ? "border-gray-800 scale-110" : "border-transparent hover:scale-105"}`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Card style */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-4">
        <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2"><Monitor size={15} className="text-supernova-500" /> Estilo de tarjetas</h4>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-2">Bordes redondeados</label>
          <div className="grid grid-cols-5 gap-1.5">
            {CARD_RADII.map(({ val, label }) => (
              <button key={val} type="button"
                onClick={() => set("cardRadius", val)}
                className={`py-2 text-xs font-semibold border-2 transition flex flex-col items-center gap-1 ${theme.cardRadius === val ? "border-supernova-500 bg-supernova-50 text-supernova-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                style={{ borderRadius: val === "none" ? "4px" : val === "md" ? "8px" : val === "xl" ? "12px" : val === "2xl" ? "16px" : "24px" }}
              >
                <span>{label.split(" ")[0]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">Sombra en tarjetas</p>
            <p className="text-xs text-gray-400 mt-0.5">Aplica sombra suave en los contenedores</p>
          </div>
          <Toggle value={theme.cardShadow ?? true} onChange={v => set("cardShadow", v)} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">Títulos sobre imágenes</p>
            <p className="text-xs text-gray-400 mt-0.5">Muestra el nombre de la tienda en la imagen</p>
          </div>
          <Toggle value={theme.showCardTitles ?? true} onChange={v => set("showCardTitles", v)} />
        </div>
      </div>
    </div>
  );
}

// â”€â”€ Main component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export default function LandingManager() {
  const [config, setConfig]       = useState(DEFAULT_CONFIG);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [tab, setTab]             = useState("blocks"); // "blocks" | "theme"
  const [showPalette, setShowPalette] = useState(false);
  const [expandedId, setExpandedId]   = useState(null);
  const paletteRef = useRef(null);

  // â”€â”€ Load â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    Promise.all([API.get("/settings/landing"), API.get("/categories")])
      .then(([sRes, cRes]) => {
        const saved = sRes.data?.data?.config ?? {};
        setConfig({
          ...DEFAULT_CONFIG,
          ...saved,
          sections: saved.sections ?? [],
          blocks:   Array.isArray(saved.blocks) ? saved.blocks : [],
          theme:    { ...DEFAULT_THEME, ...(saved.theme ?? {}) },
        });
        setCategories(cRes.data?.data?.categories ?? []);
      })
      .catch(() => toast.error("Error cargando configuración"))
      .finally(() => setLoading(false));
  }, []);

  // â”€â”€ Close palette on outside click â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const handler = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) setShowPalette(false);
    };
    if (showPalette) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPalette]);

  // â”€â”€ Block actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const addBlock = useCallback((type) => {
    const newBlock = {
      id:      uid(),
      type,
      visible: true,
      config:  { ...(DEFAULT_BLOCK_CONFIG[type] ?? {}) },
    };
    setConfig(p => ({ ...p, blocks: [...p.blocks, newBlock] }));
    setExpandedId(["events_hero","auctions_banner","products_grid"].includes(type) ? null : newBlock.id);
  }, []);

  const updateBlock = useCallback((updated) => {
    setConfig(p => ({ ...p, blocks: p.blocks.map(b => b.id === updated.id ? updated : b) }));
  }, []);

  const toggleBlock = useCallback((id) => {
    setConfig(p => ({ ...p, blocks: p.blocks.map(b => b.id === id ? { ...b, visible: !b.visible } : b) }));
  }, []);

  const deleteBlock = useCallback((id) => {
    setConfig(p => ({ ...p, blocks: p.blocks.filter(b => b.id !== id) }));
    setExpandedId(prev => prev === id ? null : prev);
  }, []);

  const duplicateBlock = useCallback((block) => {
    const dup = { ...block, id: uid() };
    setConfig(p => {
      const idx = p.blocks.findIndex(b => b.id === block.id);
      const next = [...p.blocks];
      next.splice(idx + 1, 0, dup);
      return { ...p, blocks: next };
    });
  }, []);

  // â”€â”€ Save â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleSave = async () => {
    setSaving(true);
    try {
      // Sincronizar showCardTitles del tema al nivel raíz para que Landing.jsx lo lea
      const payload = {
        ...config,
        showCardTitles: config.theme?.showCardTitles ?? config.showCardTitles ?? true,
      };
      await API.put("/settings/landing", payload);
      toast.success("Configuración guardada âœ“");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // â”€â”€ Loading â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  const blocks = config.blocks ?? [];
  const theme  = config.theme  ?? DEFAULT_THEME;
  const useBlocks = config.useBlocks ?? false;

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-10">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-supernova-100 rounded-2xl flex items-center justify-center shrink-0">
          <LayoutDashboard size={20} className="text-supernova-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-black text-gray-800">Builder del Landing</h3>
          <p className="text-xs text-gray-400">Diseña la página principal de la app</p>
        </div>
        <a
          href={process.env.REACT_APP_FRONTEND_URL ?? "/"}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition"
        >
          <ExternalLink size={12} /> Ver landing
        </a>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-supernova-500 hover:bg-supernova-600 text-white rounded-xl font-bold text-sm transition shadow-sm disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>

      {/* â”€â”€ Mode toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className={`rounded-2xl border p-4 flex items-center justify-between transition ${useBlocks ? "bg-supernova-50 border-supernova-200" : "bg-white border-gray-200"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${useBlocks ? "bg-supernova-500" : "bg-gray-200"}`}>
            {useBlocks ? <Sparkles size={16} className="text-white" /> : <ToggleLeft size={16} className="text-gray-500" />}
          </div>
          <div>
            <p className="text-sm font-bold text-gray-800">
              {useBlocks ? "Modo Builder activo" : "Modo Clásico activo"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {useBlocks
                ? "El landing usa los bloques configurados abajo"
                : "El landing usa las secciones por categoría del modo clásico"
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold ${!useBlocks ? "text-gray-700" : "text-gray-400"}`}>Clásico</span>
          <Toggle
            value={useBlocks}
            onChange={v => setConfig(p => ({ ...p, useBlocks: v }))}
          />
          <span className={`text-xs font-semibold ${useBlocks ? "text-supernova-600" : "text-gray-400"}`}>Builder</span>
        </div>
      </div>

      {/* â”€â”€ Tabs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl">
        {[
          { key: "blocks", label: useBlocks ? "Bloques" : "Secciones", Icon: Layers },
          { key: "theme",  label: "Tema & Estilo", Icon: Palette },
        ].map(({ key, label, Icon }) => (
          <button key={key} type="button"
            onClick={() => setTab(key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition ${tab === key ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* â”€â”€ Blocks tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {tab === "blocks" && (
        <>
          {!useBlocks ? (
            <ClassicMode config={config} setConfig={setConfig} categories={categories} />
          ) : (
            <div className="space-y-3">
              {/* Add block button */}
              <div className="relative" ref={paletteRef}>
                <button
                  type="button"
                  onClick={() => setShowPalette(p => !p)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-supernova-300 bg-supernova-50 hover:bg-supernova-100 text-supernova-700 font-bold text-sm transition"
                >
                  <Plus size={16} /> Agregar bloque
                  <ChevronDown size={14} className={`transition-transform ${showPalette ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {showPalette && (
                    <BlockPalette onAdd={addBlock} onClose={() => setShowPalette(false)} />
                  )}
                </AnimatePresence>
              </div>

              {/* Block list */}
              {blocks.length === 0 ? (
                <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 py-16 flex flex-col items-center justify-center text-center">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                    <Layers size={24} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-bold text-gray-400">No hay bloques todavía</p>
                  <p className="text-xs text-gray-300 mt-1">Haz clic en "Agregar bloque" para empezar a diseñar</p>
                </div>
              ) : (
                <Reorder.Group
                  axis="y"
                  values={blocks}
                  onReorder={(newOrder) => setConfig(p => ({ ...p, blocks: newOrder }))}
                  className="space-y-2"
                >
                  {blocks.map(block => (
                    <BlockCard
                      key={block.id}
                      block={block}
                      onToggle={() => toggleBlock(block.id)}
                      onDelete={() => deleteBlock(block.id)}
                      onChange={updateBlock}
                      onDuplicate={() => duplicateBlock(block)}
                      categories={categories}
                      expandedId={expandedId}
                      setExpandedId={setExpandedId}
                    />
                  ))}
                </Reorder.Group>
              )}

              {/* Info */}
              {blocks.length > 0 && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3">
                  <Check size={14} className="text-blue-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-600">
                    Arrastra los bloques para reordenarlos. Los cambios se aplican en el landing cuando guardes.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* â”€â”€ Theme tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {tab === "theme" && (
        <ThemeTab theme={theme} onChange={(t) => setConfig(p => ({ ...p, theme: t }))} />
      )}

      {/* â”€â”€ Save (bottom) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-supernova-500 hover:bg-supernova-600 text-white rounded-xl font-bold transition shadow-sm disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
